import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  output,
  TemplateRef,
  viewChild,
  viewChildren,
} from '@angular/core';

import { PropertiesConfigInputTableColumnModel } from '@blaze-shared/elements-domain';

import { ColumnType, TableVirtualScrollFacade } from '../virtual-scroll';

@Component({
  selector: 'be-table-header',
  templateUrl: './table-header.component.html',
  styleUrls: ['./table-header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class TableHeaderComponent {
  private readonly tableVirtualScrollFacade = inject(TableVirtualScrollFacade);

  displayedColumns = input<string[]>([]);
  displayedColumnsDef = input<PropertiesConfigInputTableColumnModel[]>([]);
  allColumns = input<PropertiesConfigInputTableColumnModel[]>([]);
  actionsColumnName = input('');
  selectColumnName = input('');
  columnsVirtualScrollEnabled = input(true);
  lastColumnAlwaysVisible = input(false);
  isAnyRowSelected = input(false);
  isAllSelected = input(false);
  hasSelection = input(false);
  tableWidth = input.required<number>();

  resizeColumns = output<PropertiesConfigInputTableColumnModel[]>();
  toggleSelectAll = output();
  columnsSizeChange = output<{
    left: number[];
    scrollable: number[];
    right: number[];
  }>();

  selectTemplate = viewChild<TemplateRef<unknown>>('selectTemplate');
  actionsTemplate = viewChild<TemplateRef<unknown>>('actionsTemplate');
  columnTemplate = viewChild<TemplateRef<unknown>>('columnTemplate');

  cells = viewChildren<ElementRef<HTMLElement>>('cell');

  leftColumns = this.tableVirtualScrollFacade.columns('left');
  rightColumns = this.tableVirtualScrollFacade.columns('right');
  columns = this.tableVirtualScrollFacade.columns('scroll');

  leftGridTemplateColumns = this.tableVirtualScrollFacade.gridTemplateColumns('left');
  rightGridTemplateColumns = computed(() => {
    return this.tableVirtualScrollFacade.lastColumnAlwaysVisible() &&
      this.tableVirtualScrollFacade.hasActionsColumn()
      ? `${this.tableVirtualScrollFacade.actionsCellMaxWidth()}px`
      : null;
  });

  gridTemplateColumns = this.tableVirtualScrollFacade.gridTemplateColumns('scroll');

  allColumnsCount = this.tableVirtualScrollFacade.displayedColumnsCount;
  rowWidth = this.tableVirtualScrollFacade.headerRowWidth;
  gridTemplateColumnsRow = this.tableVirtualScrollFacade.headerRowGridTemplateColumns;

  leftColumnsCount = computed(() => this.leftColumns().length);
  columnsCount = computed(() => this.columns().length);

  constructor() {
    effect(() => {
      const cells = this.cells();
      this.tableVirtualScrollFacade.updateHeaderCells(cells.map((cell) => cell.nativeElement));
    });
  }

  getTemplate(columnType: ColumnType): TemplateRef<unknown> {
    let template: TemplateRef<unknown> | undefined;
    switch (columnType) {
      case 'select':
        template = this.selectTemplate();
        break;
      case 'actions':
        template = this.actionsTemplate();
        break;
      default:
        template = this.columnTemplate();
        break;
    }

    if (!template) {
      throw new Error(`Missing template for column ${columnType}`);
    }

    return template;
  }

  onResize(columns: PropertiesConfigInputTableColumnModel[]): void {
    this.resizeColumns.emit(columns);
  }
}
